import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { date, inr, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Field, Modal, Notice, SearchInput, StatusBadge, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

/** Mirrors membershipFrom() in the API — one rule, computed server-side. */
interface Membership {
  status: 'NONE' | 'EXPIRED' | 'ACTIVE';
  endsAt: string | null;
  daysRemaining: number;
  needsRenewal: boolean;
  referralRewardsActive: boolean;
}

interface AgentRow {
  id: string;
  depth: number;
  referralCode: string;
  treePath: string;
  isActive: boolean;
  enrolledAt: string;
  user: { id: string; mobile: string; displayName: string | null; kycStatus: string; isBlocked: boolean };
  sponsor: { id: string; referralCode: string; partnerId: string | null; user: { displayName: string | null } } | null;
  membership: Membership;
  _count: { downline: number; crmCustomers: number };
  feeWaivedAt: string | null;
  feeWaivedReason: string | null;
  registrationPayment: { status: string; capturedAt: string | null; gatewayPaymentId: string | null } | null;
  /** When the paid membership runs out; null when waived or unpaid. */
  membershipExpiresAt: string | null;
  activatedAt: string | null;
  slots: { total: number; used: number; remaining: number };
}

/** The ₹2,000 membership: the partner area opens only once it is paid or waived. */
function FeeCell({ r }: { r: AgentRow }) {
  const expired = r.membershipExpiresAt != null && new Date(r.membershipExpiresAt).getTime() <= Date.now();
  if (r.registrationPayment?.status === 'CAPTURED') {
    return (
      <div>
        {expired ? (
          <Badge tone="red" dot>Expired</Badge>
        ) : (
          <Badge tone="green" dot>Paid</Badge>
        )}
        <div className="cell-muted cell-mono">{r.registrationPayment.gatewayPaymentId ?? ''}</div>
        {r.activatedAt && <div className="cell-muted">Activated {date(r.activatedAt)}</div>}
        {r.membershipExpiresAt && (
          <div className="cell-muted">
            {expired ? 'Expired' : 'Valid to'} {date(r.membershipExpiresAt)}
          </div>
        )}
      </div>
    );
  }
  if (r.feeWaivedAt) {
    return (
      <div>
        <Badge tone="blue" dot>Waived</Badge>
        <div className="cell-muted">{r.feeWaivedReason}</div>
        {r.activatedAt && <div className="cell-muted">Since {date(r.activatedAt)} · no expiry</div>}
      </div>
    );
  }
  return (
    <div>
      <Badge tone="amber" dot>Not paid</Badge>
      <div className="cell-muted">Dashboard closed</div>
    </div>
  );
}

interface SubPlan {
  id: string;
  code: string;
  label: string;
  durationDays: number;
  pricePaise: number;
}

/**
 * The membership cell.
 *
 * Three states an operator has to tell apart, so they get three colours and
 * three sentences. "Never subscribed" is not "lapsed": one can be chased for
 * a renewal, the other was never collected in the first place.
 */
function MembershipCell({ m }: { m: Membership }) {
  const tone: BadgeTone =
    m.status === 'NONE' ? 'neutral' : m.status === 'EXPIRED' ? 'red' : m.needsRenewal ? 'amber' : 'green';
  const label =
    m.status === 'NONE' ? 'Never subscribed' : m.status === 'EXPIRED' ? 'Lapsed' : m.needsRenewal ? 'Due soon' : 'Active';

  return (
    <div>
      <Badge tone={tone} dot>
        {label}
      </Badge>
      <div className="cell-muted" style={{ marginTop: 3 }}>
        {m.endsAt === null
          ? 'No membership on record'
          : m.status === 'EXPIRED'
            ? `Expired ${date(m.endsAt)}`
            : `Valid to ${date(m.endsAt)} · ${m.daysRemaining}d left`}
      </div>
    </div>
  );
}

/** v2 §12–15 "Globaxis Partners" — the agent genealogy of FR-AGT-07. */
export function AgentsPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const due = get('due') === '1';
  const toast = useToast();
  const qc = useQueryClient();

  // The renewal amount every partner on this page owes when their year is up.
  // listPlans returns only active, priced plans, so an empty result is itself
  // the answer: there is nothing to collect yet.
  const agentPlan = useQuery({
    queryKey: ['sub-plans', 'AGENT'],
    queryFn: () => api.get<SubPlan[]>('/subscriptions/plans?role=AGENT'),
  });
  const renewal = agentPlan.data?.[0];

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/admin/agents/${id}/active`, { isActive }),
    onSuccess: (_d, v) => {
      toast(v.isActive ? 'Partner reactivated' : 'Partner suspended', 'success');
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const [waiving, setWaiving] = useState<AgentRow | null>(null);
  const [reason, setReason] = useState('');
  const waiver = useMutation({
    mutationFn: ({ id, waive }: { id: string; waive: boolean }) =>
      waive ? api.post(`/admin/agents/${id}/fee-waiver`, { reason: reason.trim() }) : api.del(`/admin/agents/${id}/fee-waiver`),
    onSuccess: (_, v) => {
      toast(v.waive ? 'Fee waived: their dashboard is open' : 'Waiver withdrawn: they pay to open the dashboard', 'success');
      setWaiving(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<AgentRow>> = [
    {
      key: 'partner',
      header: 'Partner',
      render: (r) => (
        <div>
          <div className="cell-strong">
            {r.user.displayName ?? 'Unnamed'}
            {!r.isActive && (
              <span style={{ marginLeft: 6 }}>
                <Badge tone="amber">Suspended</Badge>
              </span>
            )}
          </div>
          <div className="cell-muted">{fmtMobile(r.user.mobile)}</div>
        </div>
      ),
    },
    { key: 'code', header: 'Referral code', render: (r) => <span className="cell-mono">{r.referralCode}</span> },
    { key: 'fee', header: '₹2,000 fee', render: (r) => <FeeCell r={r} /> },
    {
      key: 'slots',
      header: 'Slots',
      render: (r) =>
        r.slots ? (
          <div>
            <div className="cell-strong">
              {r.slots.used} / {r.slots.total} used
            </div>
            <div className="cell-muted">{r.slots.remaining} left</div>
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    { key: 'membership', header: 'Membership', render: (r) => <MembershipCell m={r.membership} /> },
    {
      key: 'rewards',
      header: 'Referral rewards',
      render: (r) =>
        r.membership.referralRewardsActive ? (
          <Badge tone="green">Earning</Badge>
        ) : (
          // Not a styling choice: creditReferralOnFirstPayment tests the same
          // condition and skips the bonus, so this is what the ledger will do
          // when the next member below this partner pays.
          <Badge tone="red" dot>
            Paused
          </Badge>
        ),
    },
    { key: 'depth', header: 'Depth', numeric: true, render: (r) => r.depth },
    {
      key: 'sponsor',
      header: 'Sponsor',
      render: (r) =>
        r.sponsor ? (
          <span>
            {r.sponsor.user.displayName ?? '—'}{' '}
            <span className="cell-mono">
              {r.sponsor.partnerId ? `${r.sponsor.partnerId} · ` : ''}
              {r.sponsor.referralCode}
            </span>
          </span>
        ) : (
          <Badge tone="purple">Root</Badge>
        ),
    },
    { key: 'downline', header: 'Direct downline', numeric: true, render: (r) => num(r._count.downline) },
    { key: 'kyc', header: 'KYC', render: (r) => <StatusBadge value={r.user.kycStatus} /> },
    { key: 'joined', header: 'Enrolled', render: (r) => <span className="cell-muted">{date(r.enrolledAt)}</span> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="cell-actions">
          <Link to={`/users/${r.user.id}`} onClick={(e) => e.stopPropagation()}>
            <Button size="sm">Account</Button>
          </Link>
          <Button
            size="sm"
            variant={r.isActive ? 'ghost' : 'success'}
            loading={setActive.isPending && setActive.variables?.id === r.id}
            onClick={() => setActive.mutate({ id: r.id, isActive: !r.isActive })}
          >
            {r.isActive ? 'Suspend' : 'Reactivate'}
          </Button>
          {r.registrationPayment?.status !== 'CAPTURED' &&
            (r.feeWaivedAt ? (
              <Button
                size="sm"
                variant="ghost"
                loading={waiver.isPending && waiver.variables?.id === r.id}
                onClick={() => waiver.mutate({ id: r.id, waive: false })}
              >
                Withdraw waiver
              </Button>
            ) : (
              <Button size="sm" onClick={() => setWaiving(r)}>
                Waive fee
              </Button>
            ))}
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      {!agentPlan.isLoading && !renewal && (
        <Notice tone="warn" title="No renewal amount is configured">
          A partner membership runs for a year and then has to be renewed, but there is no active, priced agent plan —
          so the API refuses every renewal with <span className="cell-mono">PLAN_PRICE_UNSET</span> and nothing can be
          collected. Set the price and activate the plan in <Link to="/plans">Plans →</Link>
        </Notice>
      )}

      <Notice tone="warn" title="Bonus payouts are on hold">
        Referral and renewal bonuses accrue to the ledger and are fully auditable, but no money is disbursed while the
        referral structure is under legal review (PRS v2 §0.2a). <Link to="/bonuses">See the ledger →</Link>
      </Notice>

      <DataTable<AgentRow>
        queryKey={['agents', search, due]}
        path="/admin/agents"
        params={{ search, renewalDue: due ? 'true' : '' }}
        columns={columns}
        emptyIcon="⚯"
        emptyTitle={due ? 'Everyone is paid up' : 'No partners yet'}
        emptyBody={
          due
            ? 'No partner is lapsed, unsubscribed, or inside the renewal window.'
            : 'Partners can only be enrolled by an existing partner — there is no self-registration (FR-AGT-01).'
        }
        footer={(page) => {
          const dueSoon = page.meta.dueSoon as number | undefined;
          const lapsed = page.meta.lapsed as number | undefined;
          const days = (page.meta.renewalWarningDays as number | undefined) ?? 30;
          if (dueSoon === undefined || lapsed === undefined) return null;
          return (
            <div
              className="row row--wrap"
              style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s5)' }}
            >
              <span className="row" style={{ gap: 7 }}>
                <Badge tone="red" dot>
                  Lapsed or never subscribed
                </Badge>
                <strong>{num(lapsed)}</strong>
              </span>
              <span className="row" style={{ gap: 7 }}>
                <Badge tone="amber" dot>
                  Due within {days} days
                </Badge>
                <strong>{num(dueSoon)}</strong>
              </span>
              {renewal && (
                <span className="cell-muted">
                  Renewal is {inr(renewal.pricePaise)} for {renewal.durationDays} days ({renewal.code}), before GST.
                </span>
              )}
            </div>
          );
        }}
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <Chips
              value={due ? 'due' : 'all'}
              options={[
                { value: 'all', label: 'All partners' },
                { value: 'due', label: 'Due for renewal' },
              ]}
              onChange={(v) => set('due', v === 'due' ? '1' : '')}
            />
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name, mobile or code…" />
          </div>
        }
      />
      {waiving && (
        <Modal
          title={`Waive the ₹2,000 fee for ${waiving.user.displayName ?? fmtMobile(waiving.user.mobile)}`}
          sub="Their partner dashboard opens without paying. Recorded in the audit log, and can be withdrawn."
          onClose={() => setWaiving(null)}
          footer={
            <>
              <Button onClick={() => setWaiving(null)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={reason.trim().length < 3}
                loading={waiver.isPending}
                onClick={() => waiver.mutate({ id: waiving.id, waive: true })}
              >
                Waive fee
              </Button>
            </>
          }
        >
          <Field label="Reason" hint="For example: company root account.">
            <Textarea rows={2} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Modal>
      )}
    </div>
  );
}
